import React from 'react';

/**
 * WordRush brand mark + wordmark + lockup + splash.
 * Ported from .nookli/designs/hifi-logo.jsx so the brand is consistent
 * across splash, login, and the home top bar. The mark is a 4-tile SVG
 * forming a "W zigzag" (W → R → U → ! across two rows).
 */

interface MarkProps {
  size?: number;
  animated?: boolean;
  variant?: 'solid' | 'mono' | 'inverted';
}

export function WordRushMark({ size = 36, animated = false, variant = 'solid' }: MarkProps) {
  const bg = variant === 'inverted' ? '#FF5A1F' : 'transparent';
  const tileFill = variant === 'mono' ? 'var(--ink)' : variant === 'inverted' ? 'white' : 'var(--ink)';
  const accentFill = variant === 'mono' ? 'var(--ink)' : variant === 'inverted' ? 'white' : '#FF5A1F';
  const innerW = size * 0.78;

  return (
    <div
      className={animated ? 'wr-mark-anim' : ''}
      style={{
        width: size,
        height: size,
        background: bg,
        borderRadius: size * 0.24,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg width={innerW} height={innerW} viewBox="0 0 32 32" fill="none">
        {/* Tile 1 — top-left, ink */}
        <rect x="2" y="2" width="11" height="11" rx="2.5" fill={tileFill} />
        <text x="7.5" y="11" textAnchor="middle" fontFamily="Bricolage Grotesque, sans-serif" fontWeight="800" fontSize="9"
          fill={variant === 'inverted' ? '#FF5A1F' : 'white'} style={{ letterSpacing: '-0.04em' }}>W</text>

        {/* Tile 2 — top-right, accent */}
        <rect x="19" y="2" width="11" height="11" rx="2.5" fill={accentFill} />
        <text x="24.5" y="11" textAnchor="middle" fontFamily="Bricolage Grotesque, sans-serif" fontWeight="800" fontSize="9"
          fill="white" style={{ letterSpacing: '-0.04em' }}>R</text>

        {/* Tile 3 — bottom-left, accent */}
        <rect x="2" y="19" width="11" height="11" rx="2.5" fill={accentFill} />
        <text x="7.5" y="28" textAnchor="middle" fontFamily="Bricolage Grotesque, sans-serif" fontWeight="800" fontSize="9"
          fill="white" style={{ letterSpacing: '-0.04em' }}>U</text>

        {/* Tile 4 — bottom-right, ink */}
        <rect x="19" y="19" width="11" height="11" rx="2.5" fill={tileFill} />
        <text x="24.5" y="28" textAnchor="middle" fontFamily="Bricolage Grotesque, sans-serif" fontWeight="800" fontSize="9"
          fill={variant === 'inverted' ? '#FF5A1F' : 'white'} style={{ letterSpacing: '-0.04em' }}>!</text>

        {/* W-zigzag connector */}
        <path d="M 7.5 13 L 7.5 17 L 24.5 17 L 24.5 19" stroke={accentFill} strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.5" />
      </svg>
    </div>
  );
}

interface WordmarkProps {
  size?: number;
  animated?: boolean;
  showByline?: boolean;
  byline?: string;
  color?: string;
  align?: 'row' | 'stack';
}

export function WordRushWordmark({
  size = 22,
  animated = false,
  showByline = true,
  byline = 'by Nookli',
  color,
  align = 'row',
}: WordmarkProps) {
  const letters = 'wordrush'.split('');
  const splitAt = 4;
  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: align === 'stack' ? 'column' : 'row',
        alignItems: align === 'stack' ? 'flex-start' : 'baseline',
        gap: align === 'stack' ? 0 : 8,
        lineHeight: 1,
      }}
    >
      <div
        style={{
          fontFamily: 'Bricolage Grotesque, sans-serif',
          fontWeight: 800,
          fontSize: size,
          letterSpacing: '-0.045em',
          color: color || 'var(--ink)',
        }}
      >
        {letters.slice(0, splitAt).map((c, i) => (
          <span key={i} className={animated ? 'wr-letter' : ''} style={{ animationDelay: animated ? `${i * 50}ms` : '0s' }}>
            {c}
          </span>
        ))}
        <span className={animated ? 'wr-rush' : ''} style={{ color: 'var(--accent)' }}>
          {letters.slice(splitAt).map((c, i) => (
            <span
              key={i}
              className={animated ? 'wr-letter' : ''}
              style={{ animationDelay: animated ? `${(splitAt + i) * 50}ms` : '0s', color: 'var(--accent)' }}
            >
              {c}
            </span>
          ))}
        </span>
      </div>
      {showByline && (
        <div
          className={animated ? 'wr-nookli-anim' : ''}
          style={{
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            fontSize: Math.max(9, size * 0.42),
            fontWeight: 500,
            color: color === 'white' ? 'rgba(255,255,255,0.65)' : 'var(--ink-3)',
            letterSpacing: '0.08em',
            textTransform: 'lowercase',
            marginTop: align === 'stack' ? 4 : 0,
          }}
        >
          {align === 'stack' ? '— ' : ''}{byline}
        </div>
      )}
    </div>
  );
}

interface LockupProps {
  size?: number;
  animated?: boolean;
  showByline?: boolean;
  color?: string;
  align?: 'row' | 'stack';
}

export function WordRushLockup({ size = 28, animated = false, showByline = true, color, align = 'row' }: LockupProps) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: size * 0.35 }}>
      <WordRushMark size={size * 1.3} animated={animated} variant={color === 'white' ? 'inverted' : 'solid'} />
      <WordRushWordmark size={size} animated={animated} showByline={showByline} color={color} align={align} />
    </div>
  );
}

interface SplashProps {
  size?: 'sm' | 'md' | 'lg';
}

/** Animated splash mark + stacked wordmark. Centered. */
export function WordRushSplash({ size = 'lg' }: SplashProps) {
  const w = size === 'lg' ? 64 : size === 'md' ? 52 : 44;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        padding: '40px 0',
      }}
    >
      <div
        style={{
          position: 'relative',
          animation: 'wr-pulse-glow 2.4s ease-in-out infinite',
          borderRadius: w * 0.24,
        }}
      >
        <WordRushMark size={w * 1.6} animated />
      </div>
      <WordRushWordmark size={w * 0.6} animated showByline align="stack" />
    </div>
  );
}

/* Inject logo animation keyframes once. */
const styleId = 'wr-logo-anims';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const s = document.createElement('style');
  s.id = styleId;
  s.textContent = `
    @keyframes wr-letter-rise {
      0% { transform: translateY(14px) scale(0.6); opacity: 0; }
      60% { transform: translateY(-3px) scale(1.05); opacity: 1; }
      100% { transform: translateY(0) scale(1); opacity: 1; }
    }
    @keyframes wr-rush-line {
      0% { transform: scaleX(0); opacity: 0; transform-origin: left; }
      40% { transform: scaleX(1); opacity: 1; transform-origin: left; }
      100% { transform: scaleX(1); opacity: 1; transform-origin: left; }
    }
    @keyframes wr-mark-pop {
      0% { transform: rotate(-30deg) scale(0); opacity: 0; }
      60% { transform: rotate(8deg) scale(1.1); opacity: 1; }
      100% { transform: rotate(0deg) scale(1); opacity: 1; }
    }
    @keyframes wr-nookli-fade {
      0%, 70% { opacity: 0; transform: translateY(4px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    @keyframes wr-pulse-glow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(255, 90, 31, 0); }
      50% { box-shadow: 0 0 0 8px rgba(255, 90, 31, 0.18); }
    }
    .wr-letter { display: inline-block; animation: wr-letter-rise 0.5s cubic-bezier(.34,1.4,.64,1) both; }
    .wr-rush { display: inline-block; position: relative; }
    .wr-rush::after {
      content: ''; position: absolute; left: 0; right: 0; bottom: 0.08em;
      height: 0.08em; background: var(--accent); border-radius: 4px;
      animation: wr-rush-line 0.7s ease-out 0.5s both;
    }
    .wr-mark-anim { animation: wr-mark-pop 0.6s cubic-bezier(.34,1.4,.64,1) both; }
    .wr-nookli-anim { animation: wr-nookli-fade 1.2s ease-out both; }
  `;
  document.head.appendChild(s);
}
